/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../../firebase';
import ActionPlanIntegration from '../ActionPlanIntegration';
import { useProgressTracking } from '../../hooks/useProgressTracking';
import MentorCard from '../Mentor/MentorCard';
import './GoalSidebar.css';

const GoalSidebar = ({ user, userGoal = null, businessPlan = null, expanded = true, onToggle, onOpenEntry, onOpenVisionBoard, onCreatePlan }) => {
  const [structuredEntries, setStructuredEntries] = useState({});
  const [latestInsight, setLatestInsight] = useState(null);

  // READ-ONLY subscription to the user's structured journal entries.
  useEffect(() => {
    if (!user) return;
    const entriesRef = ref(database, `structuredEntries/${user.uid}`);
    const unsubscribe = onValue(entriesRef, (snapshot) => setStructuredEntries(snapshot.val() || {}));
    return unsubscribe;
  }, [user]);

  // READ-ONLY subscription to the mentor reflections. Written ONLY by GlassEditor after
  // a successful save+reflection; this sidebar never writes.
  useEffect(() => {
    if (!user) return;
    const insightsRef = ref(database, `users/${user.uid}/insights`);
    const unsubscribe = onValue(insightsRef, (snapshot) => {
      const all = snapshot.val() || {};
      const newest = Object.values(all).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))[0] || null;
      setLatestInsight(newest);
    });
    return unsubscribe;
  }, [user]);

  const { progressData } = useProgressTracking(user, userGoal, businessPlan);

  // Get current goal/business plan info
  const getCurrentGoalInfo = () => {
    if (businessPlan) {
      return {
        type: 'Business Plan',
        title: businessPlan.title || businessPlan.businessIdea || 'Business Strategy',
        description: businessPlan.description || businessPlan.businessIdea || 'Building your business strategy',
        icon: '🏢',
        tone: 'plan'
      };
    }

    if (userGoal) {
      return {
        type: 'Goal',
        title: userGoal.title || userGoal.goal || 'Your Goal',
        description: userGoal.description || `Working towards: ${userGoal.title}`,
        icon: '🎯',
        tone: 'goal'
      };
    }

    return {
      type: 'Getting Started',
      title: 'Set Your Goal',
      description: 'Define what you want to achieve',
      icon: '✨',
      tone: 'start'
    };
  };

  const goalInfo = getCurrentGoalInfo();
  const progressPct = businessPlan ? (progressData?.overallProgress ?? 0) : 0;
  const pace = progressData?.pace;

  return (
    <>
      {/* Sidebar Toggle Button */}
      <button className="glass-sidebar-toggle" onClick={onToggle}>
        <span className={`glass-toggle-icon ${expanded ? 'expanded' : 'collapsed'}`}>
          {expanded ? '‹' : '›'}
        </span>
      </button>

      <div className={`glass-sidebar-scrim ${expanded ? 'visible' : ''}`} onClick={onToggle} aria-hidden="true" />

      <aside className={`glass-sidebar-left goal-sidebar ${expanded ? 'expanded' : 'collapsed'}`}>
        {/* Current Goal/Business Plan Header */}
        <div className="sidebar-header">
          <div className="mentor-today-slot">
            {latestInsight ? (
              <MentorCard
                state="content"
                headline={latestInsight.reveals}
                body={latestInsight.reframe}
                actionLabel={latestInsight.nextAction}
                onAction={undefined}
              />
            ) : (
              <MentorCard
                state="content"
                headline="Today"
                body="Write today's entry to receive your reflection"
              />
            )}
          </div>
          <div className={`goal-card goal-card--${goalInfo.tone}`}>
            <div className={`goal-icon goal-icon--${goalInfo.tone}`}>
              {goalInfo.icon}
            </div>
            <div className="goal-info">
              <div className="goal-type">{goalInfo.type}</div>
              <h3 className="goal-title">{goalInfo.title}</h3>
              <p className="goal-description">{goalInfo.description}</p>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="progress-section">
            <div className="progress-header">
              <span className="progress-status">{businessPlan ? 'In Progress' : 'Ready'}</span>
              <span className="progress-percentage">{`${Math.round(progressPct)}%`}</span>
            </div>
            <div className="progress-bar">
              <div
                className={`progress-fill progress-fill--${goalInfo.tone}`}
                style={{ width: `${progressPct}%` }}
              ></div>
            </div>
            <div className="progress-step">
              {businessPlan
                ? (Math.round(progressPct) === 0 ? 'Just getting started' : goalInfo.title)
                : 'Ready to start planning'}
            </div>
            {businessPlan && pace?.totalDays ? (
              <div className="progress-pace">Day {pace.day} of {pace.totalDays}</div>
            ) : null}
          </div>
        </div>

        {/* Action Todos */}
        <div className="sidebar-section todays-plan-expanded">
          {businessPlan ? (
            <ActionPlanIntegration
              user={user}
              userGoal={userGoal}
              businessPlan={businessPlan}
              selectedDate={new Date()}
              onTaskComplete={() => {}}
            />
          ) : (
            <div className="goal-sidebar-empty">
              <p>No business plan yet for this selection.</p>
              <button className="action-button primary" onClick={onCreatePlan}>Create a business plan</button>
            </div>
          )}
        </div>

        {/* Vision Board */}
        <div className="sidebar-section">
          <button className="action-button secondary" onClick={onOpenVisionBoard}>
            <span className="action-icon">🎯</span>
            <span className="action-text">Vision Board</span>
          </button>
        </div>

        {/* Previous Q&A Pills */}
        <div className="glass-previous-entries">
          <h4 className="glass-sidebar-subtitle">Recent Entries</h4>
          <div className="glass-qa-pills">
            {Object.entries(structuredEntries)
              .slice(-8) // Show last 8 entries
              .reverse()
              .map(([id, entry]) => (
                <div key={id} className="glass-qa-pill" onClick={() => onOpenEntry({ id, ...entry })}>
                  <div className="glass-pill-type">
                    {entry.primaryType === 'daily' ? '☀️' :
                     entry.primaryType === 'meeting' ? '🤝' : '🚀'}
                  </div>
                  <div className="glass-pill-content">
                    <div className="glass-pill-question">
                      {entry.primaryType === 'daily' ? 'Daily Focus' :
                       entry.primaryType === 'meeting' ? 'Meeting Notes' : 'Project Plan'}
                    </div>
                    <div className="glass-pill-answer">
                      {entry.summary?.substring(0, 60) || 'No summary'}...
                    </div>
                    <div className="glass-pill-date">
                      {new Date(entry.createdAt || entry.date).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}

            {Object.keys(structuredEntries).length === 0 && (
              <div className="glass-no-entries">
                <p>No previous entries yet. Start journaling to see your progress!</p>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default GoalSidebar;
